import { Box, Button, Stack, Typography, styled } from '@mui/material';
import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '@cnx-saas/snackbar';
import { RhfSelect } from '@cnx-saas/common-fe';
import { UserOutput, UserStatus } from '@cnx-saas/common';
import { usePasswordResetMutation } from '../../../api/tenant/mutations/send-reset-password';
import { getUserTypes } from '../type';

type EditUserFormProps = {
  user: UserOutput;
  onClose(): void;
  onSubmit(userInput: UserOutput): void;
};

export const EditUserForm = ({
  user,
  onClose,
  onSubmit,
}: EditUserFormProps) => {
  const { t } = useTranslation();
  const { control, handleSubmit } = useForm<UserOutput>({
    defaultValues: user,
  });
  const { addAlert } = useSnackbar();

  const [sendPasswordResetEmail] = usePasswordResetMutation();

  const sendEmail = async () => {
    const response = await sendPasswordResetEmail({
      variables: {
        details: {
          email: user.email,
        },
      },
    });
    if (response.errors) {
      addAlert({
        type: 'error',
        children: response.errors.toString(),
      });
    } else {
      addAlert({
        type: 'success',
        children: t('registration.edit-user-dialog.success', {
          defaultValue: 'Email has been sent',
        }),
      });
    }
  };

  const userTypes = useMemo(() => getUserTypes(), []);

  return (
    <Root onSubmit={handleSubmit(onSubmit)} sx={{ width: '100%' }}>
      <Box display={'flex'} flexDirection={'column'} gap={2}>
        <Box display={'flex'} flexDirection={'row'} mt={2}>
          <Typography fontWeight={600} mr={5} width={70}>
            {t('userManagement.form.name', { defaultValue: 'Name:' })}
          </Typography>
          <Typography mr={5}>{user.name}</Typography>
        </Box>
        <Box display={'flex'} flexDirection={'row'}>
          <Typography fontWeight={600} mr={5} width={70}>
            {t('userManagement.form.email', { defaultValue: 'Email:' })}
          </Typography>
          <Typography mr={5} flex={1}>
            {user.email}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 1,
            justifyContent: 'space-between',
          }}
        >
          <RhfSelect
            control={control}
            name="userType"
            label={t('userManagement.label.userType', {
              defaultValue: 'Account type',
            })}
            values={Object.values(userTypes)}
            getValueLabel={(userType) =>
              t(`userManagement.label.${userType}`, { defaultValue: userType })
            }
            sx={{ flex: 1 }}
          />
          <RhfSelect
            control={control}
            name="status"
            label={t('userManagement.lebel.userStatus', {
              defaultValue: 'Status',
            })}
            values={Object.values(UserStatus)}
            getValueLabel={(status) =>
              t(`userManagement.label.${status}`, { defaultValue: status })
            }
            sx={{ flex: 1 }}
          />
        </Box>
        <Box display={'flex'} flexDirection={'row'} alignItems={'center'}>
          <Typography fontWeight={600} mr={1} width={150}>
            {t('userManagement.form.passwordReset', {
              defaultValue: 'Passwort reset',
            })}
          </Typography>
          <Button onClick={() => sendEmail()}>
            {t('userManagement.form.resetLinkLabel', {
              defaultValue: 'Click here',
            })}
          </Button>
        </Box>
        <Stack flexDirection="row" justifyContent="end" gap={1}>
          <Button variant="outlined" onClick={onClose}>
            {t('userManagement.form.cancel', {
              defaultValue: 'Cancel',
            })}
          </Button>
          <Button type="submit">
            {t('userManagement.form.save', { defaultValue: 'Save' })}
          </Button>
        </Stack>
      </Box>
    </Root>
  );
};

export const Root = styled('form')({
  width: '100%',
});
